import { FMEARiskDesignElement } from "@design-controls/types";
import { DataProvider, QDataTable, QIcon, QText } from "@qualio/ui-components";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

type Props = {
  base: FMEARiskDesignElement;
  compare: FMEARiskDesignElement;
};

type Column = {
  label: string;
  base: any;
  compare: any;
};

export const FMEARisk: React.FC<Props> = ({ base, compare }) => {
  const { t } = useTranslation();
  const rows: Column[] = useMemo(() => {
    const rows: Column[] = [];

    if (base.title !== compare.title) {
      rows.push({
        label: t("diffView.fields.title"),
        base: base.title,
        compare: compare.title,
      });
    }

    if (base.category?.qri !== compare.category?.qri) {
      rows.push({
        label: t("diffView.fields.category"),
        base: base.category?.label,
        compare: compare.category?.label,
      });
    }
    if (base.evaluationMethod !== compare.evaluationMethod) {
      rows.push({
        label: t("diffView.fields.reasonableForeseeableUseMisuse"),
        base: base.evaluationMethod,
        compare: compare.evaluationMethod,
      });
    }
    if (base.failureCauses !== compare.failureCauses) {
      rows.push({
        label: t("diffView.fields.harm"),
        base: base.harm,
        compare: compare.harm,
      });
    }
    if (base.failureMode !== compare.failureMode) {
      rows.push({
        label: t("diffView.fields.hazard"),
        base: base.hazard,
        compare: compare.hazard,
      });
    }
    if (base.failureModeEffects !== compare.failureModeEffects) {
      rows.push({
        label: t("diffView.fields.hazardousSituation"),
        base: base.hazardSituation,
        compare: compare.hazardSituation,
      });
    }
    if (base.preMitigation.severity !== compare.preMitigation.severity) {
      rows.push({
        label: t("diffView.fields.initialSeverityAssessment"),
        base: base.preMitigation.severity,
        compare: compare.preMitigation.severity,
      });
    }
    if (base.preMitigation?.occurrence !== compare.preMitigation?.occurrence) {
      rows.push({
        label: t("diffView.fields.initialOccurrenceAssessment"),
        base: base.preMitigation?.occurrence,
        compare: compare.preMitigation?.occurrence,
      });
    }
    if (
      base.preMitigation?.detectability !== compare.preMitigation?.detectability
    ) {
      rows.push({
        label: t("diffView.fields.initialDetectabilityAssessment"),
        base: base.preMitigation?.detectability,
        compare: compare.preMitigation?.detectability,
      });
    }
    if (base.riskControl?.mitigation !== compare.riskControl?.mitigation) {
      rows.push({
        label: t("diffView.fields.riskControlDescription"),
        base: base.riskControl?.mitigation,
        compare: compare.riskControl?.mitigation,
      });
    }
    if (base.postMitigation?.severity !== compare.postMitigation?.severity) {
      rows.push({
        label: t("diffView.fields.finalSeverityAssessment"),
        base: base.postMitigation?.severity,
        compare: compare.postMitigation?.severity,
      });
    }
    if (
      base.postMitigation?.occurrence !== compare.postMitigation?.occurrence
    ) {
      rows.push({
        label: t("diffView.fields.finalOccurrenceAssessment"),
        base: base.postMitigation?.occurrence,
        compare: compare.postMitigation?.occurrence,
      });
    }
    if (
      base.postMitigation?.detectability !==
      compare.postMitigation?.detectability
    ) {
      rows.push({
        label: t("diffView.fields.finalDetectabilityAssessment"),
        base: base.postMitigation?.detectability,
        compare: compare.postMitigation?.detectability,
      });
    }

    return rows;
  }, [base, compare, t]);

  const columns = useMemo(() => {
    return [
      {
        accessorKey: "label",
        header: t("diffView.table.header.label"),
        meta: {
          width: "100px",
        },
        cell: ({ row: { original: row } }: { row: { original: Column } }) => (
          <QText fontWeight={600}>{row.label}</QText>
        ),
      },
      {
        accessorKey: "base",
        header: t("diffView.table.header.base"),
        meta: {
          width: "40%",
        },
        cell: ({ row: { original: row } }: { row: { original: Column } }) =>
          row.base ?? (
            <QText color="gray.500">{t("diffView.table.empty")}</QText>
          ),
      },
      {
        accessorKey: "id",
        header: t("diffView.table.header.icon"),
        meta: {
          width: "60px",
        },
        cell: () => <QIcon iconName="ArrowRight" />,
      },
      {
        accessorKey: "compare",
        header: t("diffView.table.header.compare"),
        meta: {
          width: "40%",
        },
        cell: ({ row: { original: row } }: { row: { original: Column } }) =>
          row.compare ?? (
            <QText color="gray.500">{t("diffView.table.empty")}</QText>
          ),
      },
    ];
  }, [t]);

  if (!rows) {
    return null;
  }

  return (
    <DataProvider.Fixed data={rows}>
      <QDataTable columns={columns} withHeaders={false} />
    </DataProvider.Fixed>
  );
};
